import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Inbox } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const openedRef = useRef(false);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    enabled: !!user,
    refetchInterval: 60000,
  });
  if (!user) return null;
  const items = data?.notifications || [];
  const unread = data?.unread || 0;

  const onOpenChange = async (open: boolean) => {
    if (open && unread > 0 && !openedRef.current) {
      openedRef.current = true;
      try {
        await api.post("/notifications/read-all");
        qc.invalidateQueries({ queryKey: ["notifications"] });
      } finally { openedRef.current = false; }
    }
  };

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notification-bell" aria-label="Notifications"
          className="relative text-gray-400 hover:text-white">
          <Inbox className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#fd0017] text-white font-mono text-[9px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] bg-[#121212] border-white/10 text-white p-0">
        <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Notifications</p>
          {unread > 0 && <span className="font-mono text-[10px] text-[#A78BFA]">{unread} new</span>}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-gray-600 text-xs" data-testid="notifications-empty">
              Nothing yet — story updates from the editorial desk land here.
            </p>
          ) : items.map((n: any) => (
            <button key={n.id} onClick={() => n.link && navigate(n.link)} data-testid={`notification-item-${n.id}`}
              className="w-full text-left px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.04] transition-colors">
              <div className="flex items-start gap-2">
                {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF] mt-1.5 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-white text-[13px] font-semibold leading-snug">{n.title}</p>
                  <p className="text-gray-500 text-xs mt-0.5 line-clamp-2 leading-relaxed">{n.body}</p>
                  <p className="font-mono text-[10px] text-gray-600 mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
